import { Subscription } from '../types';

/**
 * Calculate days until a subscription expires
 * @param subscription The subscription to check
 * @returns Number of days until expiration, or null if not applicable
 */
export function getDaysUntilExpiration(subscription: Subscription): number | null {
  // Check for subscriptions that can expire (active, trialing, or cancelled)
  // Cancelled subscriptions with a future period end are "pending cancellation"
  if (subscription.status !== 'active' &&
      subscription.status !== 'trial' &&
      subscription.status !== 'cancelled') {
    return null;
  }

  // If auto-renew is explicitly enabled AND status is active, subscription won't expire
  // Note: We treat undefined/null as "will expire" (disabled auto-renew)
  if (subscription.autoRenew === true && subscription.status === 'active') {
    return null;
  }

  // Get the expiration date
  const expirationDate = subscription.currentPeriodEnd || subscription.endDate;
  if (!expirationDate) {
    return null;
  }

  // Calculate days until expiration
  const expDate = new Date(expirationDate);
  const now = new Date();
  const diffTime = expDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

/**
 * Check if a subscription is expiring soon (within 10 days)
 * @param subscription The subscription to check
 * @returns true if expiring within 10 days
 */
export function isExpiringSoon(subscription: Subscription): boolean {
  const daysUntil = getDaysUntilExpiration(subscription);
  return daysUntil !== null && daysUntil >= 0 && daysUntil <= 10;
}

/**
 * Check if a subscription has already expired
 * @param subscription The subscription to check
 * @returns true if already expired
 */
export function isExpired(subscription: Subscription): boolean {
  const daysUntil = getDaysUntilExpiration(subscription);
  return daysUntil !== null && daysUntil < 0;
}

/**
 * Get all subscriptions that are expiring soon
 * @param subscriptions Array of subscriptions
 * @returns Array of subscriptions expiring within 10 days
 */
export function getExpiringSoonSubscriptions(subscriptions: Subscription[]): Subscription[] {
  return subscriptions.filter(isExpiringSoon);
}

/**
 * Get all expired subscriptions
 * @param subscriptions Array of subscriptions
 * @returns Array of expired subscriptions
 */
export function getExpiredSubscriptions(subscriptions: Subscription[]): Subscription[] {
  return subscriptions.filter(isExpired);
}
